import { z } from 'zod';
import { TriggerSchema } from './enums.js';
// "A:2" / "B:3" — team letter + ORIGINAL spawn position 1..5 (does not change on faint).
// Summons may carry a "#N" suffix ("B:4#1") — sim/summons.ts allocateRef appends the
// smallest free N when the base form is still occupied by a live unit, so refs stay
// unique within a team (e.g. phoenix re-summoning into its own old slot).
export const UnitRefSchema = z.string().regex(/^[AB]:[1-5](#\d+)?$/);
// Unit state snapshot in battle_start event
export const UnitSnapshotSchema = z.object({
    ref: UnitRefSchema,
    unit_id: z.string(),
    star: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    hp: z.number().int(),
    atk: z.number().int(),
    position: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
});
// Each event has at minimum { turn, type } per §7.6
const BaseEventSchema = z.object({
    turn: z.number().int().nonnegative(),
});
// --- Discriminated union of all event types ---
export const BattleStartEvent = BaseEventSchema.extend({
    type: z.literal('battle_start'),
    teams: z.object({
        A: z.array(UnitSnapshotSchema),
        B: z.array(UnitSnapshotSchema),
    }),
});
export const SynergyAppliedEvent = BaseEventSchema.extend({
    type: z.literal('synergy_applied'),
    team: z.enum(['A', 'B']),
    axis: z.enum(['animal', 'element']),
    key: z.string(),
    threshold: z.number().int(),
    description: z.string(),
});
export const TurnStartEvent = BaseEventSchema.extend({
    type: z.literal('turn_start'),
});
export const StrikeEvent = BaseEventSchema.extend({
    type: z.literal('strike'),
    source: UnitRefSchema,
    target: UnitRefSchema,
    damage: z.number().int().nonnegative(),
    multiplier: z.union([z.literal(0.75), z.literal(1.0), z.literal(1.25)]),
});
export const DamageEvent = BaseEventSchema.extend({
    type: z.literal('damage'),
    source: UnitRefSchema.nullable(),
    target: UnitRefSchema,
    amount: z.number().int().nonnegative(),
    new_hp: z.number().int(),
    // multiplier present only on strike-sourced damage; absent on ability damage
    multiplier: z.union([z.literal(0.75), z.literal(1.0), z.literal(1.25)]).optional(),
    // Damage prevented by damage_reduction (stone_tortoise plate). Absent or
    // 0 when no mitigation applied. Replay handler reads this to fire a
    // shield pulse + "blocked" floater so the player sees the plate working.
    mitigated: z.number().int().nonnegative().optional(),
});
export const HealEvent = BaseEventSchema.extend({
    type: z.literal('heal'),
    source: UnitRefSchema.nullable(),
    target: UnitRefSchema,
    amount: z.number().int().nonnegative(),
    new_hp: z.number().int(),
});
export const TriggerFiredEvent = BaseEventSchema.extend({
    type: z.literal('trigger_fired'),
    source: UnitRefSchema,
    trigger: TriggerSchema,
    effect: z.string(),
});
export const StatChangedEvent = BaseEventSchema.extend({
    type: z.literal('stat_changed'),
    target: UnitRefSchema,
    stat: z.enum(['hp', 'atk']),
    delta: z.number().int(),
    new_value: z.number().int(),
    permanent: z.boolean(),
});
export const StatusAppliedEvent = BaseEventSchema.extend({
    type: z.literal('status_applied'),
    target: UnitRefSchema,
    status: z.string(),
    stacks: z.number().int().min(1).default(1),
});
export const StatusExpiredEvent = BaseEventSchema.extend({
    type: z.literal('status_expired'),
    target: UnitRefSchema,
    status: z.string(),
});
export const SummonEvent = BaseEventSchema.extend({
    type: z.literal('summon'),
    team: z.enum(['A', 'B']),
    /** Unique ref for the newly-spawned unit. The simulator allocates
     *  collision-free refs (e.g. `B:4#1` when position 4 already holds
     *  a live unit) so tokens spawned on top of an existing slot don't
     *  alias the original unit's ref — keeps the client's units map
     *  unambiguous when later events target either. */
    ref: UnitRefSchema,
    unit_id: z.string(),
    position: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    stats: z.object({ hp: z.number().int(), atk: z.number().int() }),
});
// Emitted when a trigger queued more summons than could fit on the
// active line (MAX_TEAM_LINE = 5). One event per (source, unit_id)
// group; carries the dropped count so the chronicle can report the
// honest realized outcome ("team full — 1 paper_mite_token dropped")
// instead of letting the trigger_fired description over-promise.
export const SummonCappedEvent = BaseEventSchema.extend({
    type: z.literal('summon_capped'),
    team: z.enum(['A', 'B']),
    /** Ref of the unit whose trigger queued the dropped summons. May be
     *  null when the queue came from a non-unit source (synergy effect). */
    source: UnitRefSchema.nullable(),
    unit_id: z.string(),
    dropped: z.number().int().min(1),
    reason: z.literal('team_full'),
});
export const CrumpleEvent = BaseEventSchema.extend({
    type: z.literal('crumple'),
    unit: UnitRefSchema,
});
export const SlideForwardEvent = BaseEventSchema.extend({
    type: z.literal('slide_forward'),
    team: z.enum(['A', 'B']),
    /** Surviving units' REFS in front-to-back order. Length = number of
     *  survivors. Refs (instead of positions) because positions are
     *  no longer unique once same-slot summons land — a token spawned
     *  at slot 4 keeps `position: 4` even when the original slot-4
     *  unit is still alive, so a position-array couldn't distinguish
     *  them. */
    new_order: z.array(UnitRefSchema),
});
// Two units swap their line positions mid-battle (wind_5 synergy, and
// any future swap_positions effect). Distinct from slide_forward —
// slide_forward is post-death compaction, position_swap is a tactical
// rearrangement initiated by an effect.
export const PositionSwapEvent = BaseEventSchema.extend({
    type: z.literal('position_swap'),
    team: z.enum(['A', 'B']),
    unit_a: UnitRefSchema,
    unit_b: UnitRefSchema,
});
export const TurnEndEvent = BaseEventSchema.extend({
    type: z.literal('turn_end'),
});
// Emitted when a battle-phase grant_gold effect fires (e.g. gold_fish
// on_crumple). The post-battle pipeline reads these from event_log and
// adds them to the owning player's pendingGold. The simulator itself
// stays pure — it doesn't know about shop state. `timing` mirrors the
// shop-phase grant_gold param so the post-battle pipeline can route it
// to gold-now vs gold-next-round.
export const GoldEarnedEvent = BaseEventSchema.extend({
    type: z.literal('gold_earned'),
    team: z.enum(['A', 'B']),
    unit: UnitRefSchema,
    amount: z.number().int().positive(),
    timing: z.enum(['now', 'next_round']),
});
export const BattleEndEvent = BaseEventSchema.extend({
    type: z.literal('battle_end'),
    winner: z.enum(['A', 'B', 'tie']),
    surviving_count: z.number().int().nonnegative(),
});
// --- Discriminated union ---
export const BattleEventSchema = z.discriminatedUnion('type', [
    BattleStartEvent,
    SynergyAppliedEvent,
    TurnStartEvent,
    StrikeEvent,
    DamageEvent,
    HealEvent,
    TriggerFiredEvent,
    StatChangedEvent,
    StatusAppliedEvent,
    StatusExpiredEvent,
    SummonEvent,
    SummonCappedEvent,
    CrumpleEvent,
    SlideForwardEvent,
    PositionSwapEvent,
    TurnEndEvent,
    BattleEndEvent,
    GoldEarnedEvent,
]);
// --- Battle result ---
export const BattleResultSchema = z.object({
    winner: z.enum(['A', 'B', 'tie']),
    surviving_count: z.number().int().nonnegative(),
    event_log: z.array(BattleEventSchema),
});
// --- Team input to simulator ---
export const TeamUnitInputSchema = z.object({
    unit_id: z.string(),
    star: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    position: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    // Current stats after combines (max-of-parents +1 accumulation per §4.4)
    current_stats: z.object({
        hp: z.number().int().min(1),
        atk: z.number().int().min(0),
    }),
});
export const TeamInputSchema = z.array(TeamUnitInputSchema).min(1).max(5);
//# sourceMappingURL=events.js.map